import { ChangeEvent, useEffect, useMemo, useState } from 'react'
import { faCheck } from '@fortawesome/pro-duotone-svg-icons'

import Icon from '../icon/Icon'
import { requestApplicationParagraphs } from '../../_data/renter-llm'
import { useAuth } from '../../_hooks/use-auth'
import { useImportedListings } from '../../_hooks/use-imported-listings'
import { useListingDecisions } from '../../_hooks/use-listing-decisions'
import { useQuickPassProfile } from '../../_hooks/use-quickpass-profile'
import { ApplicationParagraphsResult } from '../../_types/quickpass-llm'
import { formatRelativeTime } from '../../_utilities/relative-time'
import {
  Card,
  HeaderRow,
  HeaderText,
  Eyebrow,
  Title,
  Subtitle,
  HeaderActions,
  PrimaryButton,
  SecondaryButton,
  ParagraphList,
  ParagraphBlock,
  ParagraphHeader,
  ParagraphLabel,
  ParagraphDescription,
  ParagraphCopyButton,
  ParagraphTextarea,
  Footer,
  StampText,
  CopyAllFlash,
  StatusText,
  ErrorText,
  SkeletonLine,
  PremiumBadge,
} from './styled'
import {
  PARAGRAPH_SECTIONS,
  buildListingsContextSummary,
  buildProfileFingerprint,
  buildProfileForPrompt,
  clearCache,
  generateButtonLabelFor,
  readCache,
  writeCache,
} from './helpers'
import { ApplicationParagraphsState, ParagraphKey } from './types'

const COPIED_FLASH_MS = 1500

/**
 * Profile-page section that produces the three durable paragraphs a
 * renter pastes into rental application forms — about-me, why-good-
 * fit, additional-notes. Generates via LLM, caches per-fingerprint
 * for a week, and lets the renter edit before copy-to-clipboard.
 *
 * @returns {JSX.Element | null}
 */
const ApplicationParagraphs = (): JSX.Element | null => {
  const { profile, patchProfile } = useQuickPassProfile()
  const { quickPassUser } = useAuth()
  const { listings } = useImportedListings()
  const { tags } = useListingDecisions()

  const persistParagraphs = (result: ApplicationParagraphsResult): void => {
    void patchProfile({
      applicationParagraphs: {
        aboutMe: result.aboutMe,
        whyImAGoodFit: result.whyImAGoodFit,
        additionalNotes: result.additionalNotes,
      },
    }).catch((error: unknown) => {
      console.warn('[application-paragraphs] persist failed', error)
    })
  }

  const [state, setState] = useState<ApplicationParagraphsState>('idle')
  const [errorMessage, setErrorMessage] = useState<string>('')
  const [editable, setEditable] = useState<ApplicationParagraphsResult | null>(
    null,
  )
  const [generatedAt, setGeneratedAt] = useState<number | null>(null)
  const [didJustCopyAll, setDidJustCopyAll] = useState<boolean>(false)
  const [copiedKey, setCopiedKey] = useState<ParagraphKey | null>(null)

  const fingerprint = useMemo<string | null>(() => {
    if (profile === null) {
      return null
    }
    return buildProfileFingerprint(profile)
  }, [profile])

  useEffect(() => {
    if (didJustCopyAll === false && copiedKey === null) {
      return
    }
    const handle = setTimeout(() => {
      setDidJustCopyAll(false)
      setCopiedKey(null)
    }, COPIED_FLASH_MS)
    return () => {
      clearTimeout(handle)
    }
  }, [didJustCopyAll, copiedKey])

  useEffect(() => {
    const userId = quickPassUser?.id ?? ''
    if (userId === '' || fingerprint === null) {
      return
    }

    const cached = readCache(userId, fingerprint)
    if (cached !== null) {
      setEditable(cached.result)
      setGeneratedAt(cached.generatedAt)
      setState('ready')
    }
  }, [quickPassUser, fingerprint])

  if (profile === null) {
    return null
  }

  const userId = quickPassUser?.id ?? ''

  const handleGenerate = async (): Promise<void> => {
    if (fingerprint === null) {
      return
    }
    setState('loading')
    setErrorMessage('')

    try {
      const response = await requestApplicationParagraphs({
        profile: buildProfileForPrompt(profile),
        listingsContextSummary: buildListingsContextSummary(listings, tags),
      })

      if (response === null) {
        setState('offline')
        return
      }

      writeCache(userId, fingerprint, response)
      setEditable(response)
      setGeneratedAt(Date.now())
      setState('ready')
      persistParagraphs(response)
    } catch (error) {
      setState('error')
      setErrorMessage(
        error instanceof Error
          ? error.message
          : 'Could not draft your paragraphs. Try again in a sec.',
      )
    }
  }

  const handleRefresh = async (): Promise<void> => {
    if (userId !== '') {
      clearCache(userId)
    }
    await handleGenerate()
  }

  const handleParagraphChange = (
    key: ParagraphKey,
    event: ChangeEvent<HTMLTextAreaElement>,
  ): void => {
    if (editable === null) {
      return
    }
    setEditable({
      ...editable,
      [key]: event.target.value,
    })
  }

  const handleParagraphBlur = (): void => {
    if (editable === null) {
      return
    }
    persistParagraphs(editable)
  }

  const handleCopy = async (key: ParagraphKey): Promise<void> => {
    if (editable === null) {
      return
    }
    try {
      await navigator.clipboard.writeText(editable[key])
      setCopiedKey(key)
    } catch (error) {
      console.error('[application-paragraphs] copy failed', error)
    }
  }

  const handleCopyAll = async (): Promise<void> => {
    if (editable === null) {
      return
    }
    const composed = PARAGRAPH_SECTIONS.map(section => {
      return `${section.label}\n${editable[section.key]}`
    }).join('\n\n')

    try {
      await navigator.clipboard.writeText(composed)
      setDidJustCopyAll(true)
    } catch (error) {
      console.error('[application-paragraphs] copy-all failed', error)
    }
  }

  return (
    <Card>
      <HeaderRow>
        <HeaderText>
          <Eyebrow>
            Application copy <PremiumBadge>AI</PremiumBadge>
          </Eyebrow>
          <Title>Paragraphs every landlord asks for</Title>
          <Subtitle>
            Generate the three short paragraphs you&apos;ll paste into most
            rental application forms. Edit before copying — we won&apos;t make
            anything up that isn&apos;t in your profile.
          </Subtitle>
        </HeaderText>
        <HeaderActions>
          {state === 'ready' ? (
            <SecondaryButton
              type="button"
              onClick={handleRefresh}
              disabled={state !== 'ready'}
            >
              Regenerate
            </SecondaryButton>
          ) : null}
          <PrimaryButton
            type="button"
            onClick={handleGenerate}
            disabled={state === 'loading'}
          >
            {generateButtonLabelFor(state)}
          </PrimaryButton>
        </HeaderActions>
      </HeaderRow>

      {state === 'idle' ? (
        <StatusText>
          We&apos;ll use what&apos;s already on your profile — name, cities,
          budget, move-in date, take-home — to draft three paragraphs you can
          edit and reuse.
        </StatusText>
      ) : null}

      {state === 'loading' ? (
        <ParagraphList>
          <SkeletonLine style={{ width: '40%' }} />
          <SkeletonLine />
          <SkeletonLine style={{ width: '85%' }} />
        </ParagraphList>
      ) : null}

      {state === 'offline' ? (
        <StatusText>
          Backend not configured — application copy needs the LLM endpoint to be
          reachable.
        </StatusText>
      ) : null}

      {state === 'error' ? <ErrorText>{errorMessage}</ErrorText> : null}

      {state === 'ready' && editable !== null ? (
        <>
          <ParagraphList>
            {PARAGRAPH_SECTIONS.map(section => (
              <ParagraphBlock key={section.key}>
                <ParagraphHeader>
                  <ParagraphLabel>{section.label}</ParagraphLabel>
                  <ParagraphCopyButton
                    type="button"
                    onClick={() => handleCopy(section.key)}
                  >
                    {copiedKey === section.key ? 'Copied' : 'Copy'}
                  </ParagraphCopyButton>
                </ParagraphHeader>
                <ParagraphDescription>
                  {section.description}
                </ParagraphDescription>
                <ParagraphTextarea
                  value={editable[section.key]}
                  onChange={event => handleParagraphChange(section.key, event)}
                  onBlur={handleParagraphBlur}
                  aria-label={section.label}
                />
              </ParagraphBlock>
            ))}
          </ParagraphList>
          <Footer>
            <StampText>
              {generatedAt === null
                ? 'Just generated.'
                : `Generated ${formatRelativeTime(generatedAt)}.`}
            </StampText>
            <HeaderActions>
              {didJustCopyAll === true ? (
                <CopyAllFlash>
                  All copied <Icon icon={faCheck} />
                </CopyAllFlash>
              ) : null}
              <PrimaryButton type="button" onClick={handleCopyAll}>
                Copy all three
              </PrimaryButton>
            </HeaderActions>
          </Footer>
        </>
      ) : null}
    </Card>
  )
}

export default ApplicationParagraphs
